import React, { useContext, useMemo } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline/index.js';
import { calculatePossiblePaths, findSection } from '../../lib/FormHelpers.js';
import { ThemeContext } from '../../lib/ThemeContext.js';
import { hasFullBackground } from '../lib/PageHelpers.js';
import ProgressCircle from './ProgressCircle.jsx';
import { useDebounce } from '@uidotdev/usehooks';

const FormHeaderProgress = ({
    visibleSections,
    setVisibleSections,
    sections,
    form,
    answers,
    hasSubmitted,
    isOnPaymentStep,
    hasPaymentStep,
    onPaymentStepBack,
}) => {
    const { color: themeColor } = useContext(ThemeContext);

    const hideFormName = !!form?.settings?.styles?.hideFormName;

    const currentSection = findSection(
        visibleSections[visibleSections.length - 1],
        sections,
    );
    let maxPath = 1;
    let minPath = 1;

    const debouncedAnswers = useDebounce(answers, 500);
    const paths = useMemo(() => {
        if (!currentSection) {
            return [];
        }

        return calculatePossiblePaths(
            currentSection,
            sections,
            [currentSection.id],
            answers,
        ).map(p => p.length);
    }, [
        currentSection?.id,
        JSON.stringify(sections.map(s => s.section)),
        JSON.stringify(debouncedAnswers),
    ]);
    maxPath = Math.max(...paths) + visibleSections.length - 1;
    minPath = Math.min(...paths) + visibleSections.length - 1;

    if (hasSubmitted && !isOnPaymentStep) {
        return null;
    }

    if (hasPaymentStep) {
        maxPath += 1;
    }

    let numSectionsDone = visibleSections.length;
    if (isOnPaymentStep) {
        numSectionsDone = maxPath;
    }

    const progress = numSectionsDone / (maxPath + 1);
    const showBackLink = visibleSections.length > 1 || isOnPaymentStep;
    const showProgressCircle = maxPath > 1;
    const hasHeaderInfo = !hideFormName || showBackLink || showProgressCircle;

    return (
        <>
            <h1 className="visible sm:hidden text-2xl font-bold flex-1 text-center">
                {!hideFormName && form.name}
            </h1>
            <div
                className={`flex md:mr-0 md:min-w-[650px] ${!hasFullBackground() ? 'no-background-border-b border-gray-300' : ''} items-center justify-between ${hasHeaderInfo ? 'pb-4' : ''}`}
            >
                <div className="flex-1 pl-4">
                    {showBackLink && (
                        <a
                            type="button"
                            href="#"
                            className="link-no-underline flex items-center"
                            onClick={ev => {
                                ev.preventDefault();
                                if (isOnPaymentStep) {
                                    onPaymentStepBack();
                                } else {
                                    setVisibleSections(
                                        visibleSections.slice(0, -1),
                                    );
                                }
                            }}
                        >
                            <ArrowLeftIcon className="w-4 h-4 mr-2" />
                            Back
                        </a>
                    )}
                </div>
                <h1 className="hidden sm:block text-2xl font-bold flex-1 text-center">
                    {!hideFormName && form.name}
                </h1>
                <div className="flex-1 flex justify-end items-center text-right uppercase text-gray-500 sm:pr-4">
                    {showProgressCircle && (
                        <>
                            <span className="text-nowrap block">
                                Step {numSectionsDone} of {maxPath}
                            </span>
                            <ProgressCircle
                                className="ml-4"
                                color={themeColor}
                                progress={progress}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default FormHeaderProgress;
